import { useEffect, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Alert, Button, Flex, Progress, Typography } from 'antd'
import { useTranslation } from 'react-i18next'
import { completeImpression, startImpression, type AdCampaign, type GuestAccessResult } from '@/features/portal/api'
import { translatePortalError } from '@/shared/api/errors'
import { LoadingScreen } from '@/shared/ui/LoadingScreen'
import { StepHeader } from '@/shared/ui/StepHeader'

interface AdWatchStepProps {
  sessionId: string
  campaign: AdCampaign
  onAccess: (result: GuestAccessResult) => void
  /** Tiklab bo'lmaydigan xato (masalan ko'rsatish umuman boshlanmadi) — butun oqim to'xtaydi */
  onFatalError: (message: string) => void
}

/**
 * Reklama ko'rsatish — vazifa hujjatining "eng nozik joyi" (§9.1 emas, bu
 * §8): "skip" tugmasi YO'Q va bo'lmaydi. Taymer mijozda faqat KO'RSATISH
 * uchun — haqiqiy tekshiruv har doim `completeImpression` chaqiruvida
 * SERVER soati bo'yicha bajariladi (`docs/09-VENUE-PORTAL.md` §8).
 * Shuning uchun "Davom etish" tugmasi mijoz taymeri tugagach yoqiladi,
 * lekin server baribir o'zinikini tekshiradi — ikkalasi orasidagi kichik
 * farq (tarmoq kechikishi) `ad.retryHint` bilan yopiladi: tugma yana
 * bosiladi, YANGI ko'rsatish boshlanmaydi, xuddi shu nonce qayta
 * yuboriladi (`features/portal/api.ts` — `completeImpression` javadoc'i:
 * muvaffaqiyatsiz urinish nonce'ni KUYDIRMAYDI).
 */
export function AdWatchStep({ sessionId, campaign, onAccess, onFatalError }: AdWatchStepProps) {
  const { t } = useTranslation()
  const [elapsed, setElapsed] = useState(0)
  const [completeError, setCompleteError] = useState<string | null>(null)

  const startQuery = useQuery({
    queryKey: ['portal', 'impression', sessionId, campaign.id],
    queryFn: () => startImpression(sessionId, campaign.id),
    staleTime: Infinity,
    retry: false,
  })

  useEffect(() => {
    if (startQuery.isError) {
      onFatalError(translatePortalError(startQuery.error, t).message)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startQuery.isError, startQuery.error])

  useEffect(() => {
    if (!startQuery.data) return undefined
    const id = window.setInterval(() => setElapsed((s) => s + 1), 1000)
    return () => window.clearInterval(id)
  }, [startQuery.data])

  const completeMutation = useMutation({
    mutationFn: (args: { impressionId: string; nonce: string }) =>
      completeImpression(args.impressionId, args.nonce),
  })

  if (startQuery.isPending || startQuery.isError) {
    return <LoadingScreen text={t('ad.starting')} />
  }

  const impression = startQuery.data
  const minView = impression.minViewSeconds
  const ready = elapsed >= minView
  const progress = Math.min(100, Math.round((elapsed / Math.max(1, minView)) * 100))

  const handleContinue = () => {
    setCompleteError(null)
    completeMutation.mutate(
      { impressionId: impression.impressionId, nonce: impression.nonce },
      {
        onSuccess: (completed) => {
          if (completed.access) {
            onAccess(completed.access)
          } else {
            // Hujjatga ko'ra bu holat "sessiyada allaqachon kirish bor edi"
            // degani (`ImpressionCompleted` javadoc'i) — bizning oqimimizda
            // (jurnalOnly'dan keyingi birinchi reklama) sodir bo'lmasligi
            // kerak. Shunga qaramay jimgina osilib qolishdan ko'ra aniq
            // xato ko'rsatiladi.
            onFatalError(t('error.generic'))
          }
        },
        onError: () => setCompleteError(t('ad.retryHint')),
      },
    )
  }

  return (
    <Flex vertical gap={16}>
      {/*
        ⚠️ "NEGA MEN REKLAMA KO'RYAPMAN" — MEHMONNING BIRINCHI SAVOLI.

        Ilgari bu ekranda faqat `ad.by` ("… tomonidan") turgan: mehmon
        to'satdan progress-bar va begona brend nomini ko'rar, nega
        ko'rsatilayotgani ham, tugagach nima bo'lishi ham hech qayerda
        aytilmasdi. Ikkala matn (`ad.title`, `ad.subtitle`) uch tilda
        yozilgan, lekin chaqirilmagan edi.
      */}
      <StepHeader title={t('ad.title')} subtitle={t('ad.subtitle')} />

      <Typography.Text type="secondary" style={{ textAlign: 'center' }}>
        {t('ad.by', { name: campaign.advertiserName })}
      </Typography.Text>

      {/* Ijod (video/rasm) — `creativeRef` ning aniq turi (URL/kalit)
          backend hujjatida ko'rsatilmagan (`venue/api/AdCampaign.java` da
          faqat `String creativeRef` — izohsiz manba turi). Shuning uchun
          faqat http(s) manzil bo'lsa rasm sifatida ko'rsatiladi, aks
          holda neytral joy egallovchi — o'ylab topilgan render mantig'i
          emas, xavfsiz zaxira. */}
      <div
        style={{
          borderRadius: 12,
          overflow: 'hidden',
          background: 'rgba(128,128,128,0.12)',
        }}
      >
        {campaign.creativeRef?.startsWith('http') ? (
          <img src={campaign.creativeRef} alt={campaign.name} style={{ width: '100%', display: 'block' }} />
        ) : (
          <Flex align="center" justify="center" style={{ height: 180, padding: 16, textAlign: 'center' }}>
            <Typography.Text type="secondary">{campaign.name}</Typography.Text>
          </Flex>
        )}
      </div>

      {/*
        ⚠️ TUGAGAN HOLAT "DAVOM ETMOQDA" DEB YOZILARDI.

        Bu yerda `ready ? t('ad.watching') : …` turgan edi, ya'ni taymer
        TUGAGACH matn "Ko'rsatilmoqda…" (ru "Идёт показ…", en "Playing…")
        bo'lardi. Uch belgi bir vaqtda qarama-qarshi gapirardi: chiziq
        100%, tugma yonadi, matn esa hali davom etayotganini aytadi.

        Mehmon uchun oqibati aniq: u tugma yonganini ko'rmay, matnga
        ishonib KUTIB o'tiraveradi — reklama ko'rilgan, kirish tayyor,
        lekin ekran uni to'xtatib turadi.

        ⚠️ Kalit QO'SHILMADI, NOMI TUZATILDI (`ad.watching` -> `ad.done`).
        Yurayotgan holat uchun alohida matn kerak emas: uni `ad.remaining`
        soniyalar bilan allaqachon aytadi. `ad.watching` ni joyida
        qoldirish esa uch faylda o'lik kalit qoldirardi.
      */}
      <Flex vertical gap={4}>
        <Progress percent={progress} showInfo={false} />
        <Typography.Text type="secondary" style={{ textAlign: 'center' }}>
          {ready ? t('ad.done') : t('ad.remaining', { seconds: Math.max(0, minView - elapsed) })}
        </Typography.Text>
      </Flex>

      {completeError ? <Alert type="warning" showIcon message={completeError} /> : null}

      <Button
        type="primary"
        size="large"
        block
        disabled={!ready}
        loading={completeMutation.isPending}
        onClick={handleContinue}
      >
        {completeMutation.isPending ? t('ad.completing') : t('ad.ready')}
      </Button>
    </Flex>
  )
}
